
import { leereBildWeich, tauscheBildWeich } from '@theme/dp-bildtausch';
import { setzeFarbhinweis } from '@theme/dp-ohne-bild';
import { setzeVariantenVerweise } from '@theme/dp-variantenverweis';

/**
 * Schreibt Fokus und Zoom an ein Kachelbild - als Variablen und Klasse fuer
 * das Stilblatt (.dp-zoomt vergroessert ohne transform um den Fokus).
 *
 * @param {HTMLImageElement} bild
 * @param {string} fokus - "x% y%" oder leer
 * @param {string} zoom - Faktor oder leer
 */
function setzeFokusUndZoom(bild, fokus, zoom) {
  const ziel = fokus || '';
  if (bild.style.objectPosition !== ziel) bild.style.objectPosition = ziel;
  const teile = ziel.split(' ');
  bild.style.setProperty('--dp-fokus-x', teile[0] || '');
  bild.style.setProperty('--dp-fokus-y', teile[1] || '');
  bild.style.setProperty('--dp-zoom', zoom || '');
  bild.classList.toggle('dp-zoomt', !!zoom);
}

/**
 * Waehlt die Farbe einer Kachel wieder ab: Der Punkt ist nicht mehr markiert,
 * die Kachel fuehrt wieder zur Produktseite ohne Variante, und das Bild kehrt
 * zum Stand zurueck, den die Kachel vor der ersten Farbwahl zeigte (die Bahn
 * stellt dazu ihre Liste ohne Farbwahl wieder her, dp-kachelbilder.js).
 *
 * @param {Element} punkt
 * @param {Element | null} kachel
 * @param {Element | null | undefined} bild
 */
function waehleAb(punkt, kachel, bild) {
  for (const geschwister of punkt.parentElement.children) {
    geschwister.classList.remove('dp-farbe--aktiv');
  }
  if (!kachel) return;

  setzeVariantenVerweise(kachel.querySelectorAll('a.resource-card__link, a.dp-farben__mehr'), null, []);

  const hinweis = kachel.querySelector('.dp-ohne-bild--kachel');
  if (hinweis) setzeFarbhinweis(hinweis, false);
  if (!(bild instanceof HTMLImageElement)) return;

  const quelle = kachel.dataset.dpStandardSrc || '';
  const fokus = kachel.dataset.dpStandardFokus || '';
  const zoom = kachel.dataset.dpStandardZoom || '';

  // Stand die Kachel leer da (nur Bilder mit Farbmarke), gehoert die leere Flaeche samt Hinweis wieder her.
  if (!quelle) {
    if (hinweis) {
      leereBildWeich(bild);
      hinweis.classList.remove('dp-ohne-bild--weg');
    }
    return;
  }

  const setzeLage = (ziel) => {
    setzeFokusUndZoom(ziel, fokus, zoom);
    if (hinweis) hinweis.classList.add('dp-ohne-bild--weg');
  };

  if (kachel.querySelector('.dp-medien--bahn')) {
    bild.removeAttribute('srcset');
    bild.src = quelle;
    bild.classList.remove('dp-bildtausch--leer');
    setzeLage(bild);
  } else {
    tauscheBildWeich(bild, quelle, setzeLage);
  }

  // Das zweite Bild (beim Ueberfahren) gehoert zum Stand ohne Farbe und kehrt mit ihm zurueck.
  const zweites = kachel.querySelector('.resource-card__image--secondary');
  if (zweites instanceof HTMLElement) {
    zweites.classList.remove('dp-bildtausch--ab');
    zweites.style.display = '';
  }

  kachel.dispatchEvent(
    new CustomEvent('dp:zeigeBild', { bubbles: true, detail: { quelle, fokus, zoom, farbe: '', zuruecksetzen: true } })
  );
}

/**
 * Farbpunkte auf den Produktkacheln.
 *
 * Ein Tippen auf einen Punkt zeigt das Bild, das zu dieser Farbe gehoert -
 * ohne die Seite zu wechseln. Die ganze Kachel ist ein Verweis; der Klick wird
 * deshalb in der Erfassungsphase abgefangen und dort angehalten.
 *
 * Das zweite Bild der Karte (das beim Ueberfahren erscheint) wird dabei
 * ausgeblendet: es gehoert zur zuvor gezeigten Farbe und passte sonst nicht
 * mehr zum Bild darunter.
 */
document.addEventListener(
  'click',
  (ereignis) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;

    // Der Mehr-Verweis behaelt seinen Weg zur Produktseite.
    if (ziel.closest('.dp-farben__mehr')) return;

    let punkt = ziel.closest('.dp-farbe');

    /*
     * Tipp im Fangbereich der Reihe (unsichtbares Polster um die Punkte):
     * statt zur Produktseite durchzurutschen, gilt er dem naechstgelegenen
     * Punkt. Die Reihe faengt so auch knappe Fehlgriffe auf.
     */
    if (!punkt && ziel.closest('.dp-farben') && ereignis instanceof MouseEvent) {
      let naechster = null;
      let kleinster = Infinity;
      for (const kandidat of ziel.closest('.dp-farben').querySelectorAll('.dp-farbe')) {
        if (!(kandidat instanceof HTMLElement) || kandidat.offsetParent === null) continue;
        const rand = kandidat.getBoundingClientRect();
        const dx = ereignis.clientX - (rand.left + rand.width / 2);
        const dy = ereignis.clientY - (rand.top + rand.height / 2);
        const abstand = dx * dx + dy * dy;
        if (abstand < kleinster) {
          kleinster = abstand;
          naechster = kandidat;
        }
      }
      punkt = naechster;
    }

    if (!punkt) return;

    ereignis.preventDefault();
    ereignis.stopPropagation();

    /*
     * Die Kachel, zu der der Punkt gehoert. Auf den Kategorieseiten ist das
     * .dp-product, in der Schublade der Eintrag der Vorschau - beide umfassen
     * genau eine Karte.
     */
    const kachel = punkt.closest('.dp-product, .menu-drawer__featured-content-list-item');
    const bild = kachel?.querySelector('.resource-card__image:not(.resource-card__image--secondary)');
    const adresse = punkt.dataset.dpBild;

    /*
     * Der Stand der Kachel, bevor die erste Farbe ihn ersetzt: Bild samt Fokus
     * und Zoom. Das Abwaehlen stellt ihn wieder her.
     */
    if (kachel && bild && kachel.dataset.dpStandardSrc === undefined) {
      kachel.dataset.dpStandardSrc = bild.getAttribute('src') || '';
      kachel.dataset.dpStandardFokus = bild.style.objectPosition || '';
      kachel.dataset.dpStandardZoom = bild.style.getPropertyValue('--dp-zoom') || '';
    }

    // Ein zweiter Tipp auf den gewaehlten Punkt waehlt die Farbe wieder ab.
    if (punkt.classList.contains('dp-farbe--aktiv')) {
      waehleAb(punkt, kachel, bild);
      return;
    }

    // Markiert wird immer - auch wenn zu der Farbe kein eigenes Bild vorliegt.
    for (const geschwister of punkt.parentElement.children) {
      geschwister.classList.toggle('dp-farbe--aktiv', geschwister === punkt);
    }

    /*
     * Die Kachel fuehrt ab jetzt zur gewaehlten Farbe: Wer danach auf die
     * Kachel tippt, landet auf der Produktseite mit dieser Variante
     * (?variant=..., dp-variantenverweis.js - derselbe Baustein wie im
     * Schnellkauf-Feld). Die Nummer steht am Punkt (dp-farbpunkte.liquid).
     * Das Schnellkauf-Feld behaelt seine eigene Wahl.
     */
    if (kachel) {
      setzeVariantenVerweise(
        kachel.querySelectorAll('a.resource-card__link, a.dp-farben__mehr'),
        punkt.dataset.dpVariante || null,
        // Gewaehlt ist nur die Farbe - Groesse und Co. bleiben auf der Produktseite offen.
        punkt.dataset.dpWahl ? [Number(punkt.dataset.dpWahl)] : []
      );
    }

    /*
     * Kachel ohne Bild ohne Farb-Marke: Ueber der leeren Bildflaeche liegt
     * der Hinweis "Noch keine Bilder" (resource-card, dp-ohne-bild--kachel).
     * Seine zweite Zeile sagt ab jetzt, dass es fuer die gewaehlte Farbe
     * keine Bilder gibt - zu sehen ist das nur, wenn die Farbe tatsaechlich
     * keins hat: Dann blendet ein zuvor gezeigtes Farbbild ab und der
     * Hinweis wieder auf.
     */
    const hinweis = kachel?.querySelector('.dp-ohne-bild--kachel');
    if (hinweis) setzeFarbhinweis(hinweis, true);
    if (hinweis && bild && !adresse) {
      leereBildWeich(bild);
      hinweis.classList.remove('dp-ohne-bild--weg');
      // Das zweite Bild (beim Ueberfahren) tritt ab wie bei jedem Farbwechsel - sonst stuende es ueber dem Hinweis.
      const zweitesBild = kachel.querySelector('.resource-card__image--secondary');
      if (zweitesBild instanceof HTMLElement && zweitesBild.style.display !== 'none') {
        zweitesBild.classList.add('dp-bildtausch--ab');
        window.setTimeout(() => {
          zweitesBild.style.display = 'none';
        }, 400);
      }
      // Liegt die Wischbahn darueber, blendet sie ab und gibt den Hinweis frei (dp-kachelbilder.js).
      kachel.dispatchEvent(new CustomEvent('dp:zeigeBild', { bubbles: true, detail: { leer: true, farbe: punkt.dataset.dpFarbe || '' } }));
      return;
    }

    if (!bild || !adresse) return;

    // Fokus und Zoom des Farbbildes (Alt-Marke "zoom:XX") wandern mit - leer heisst Mitte und kein Zoom.
    const fokus = punkt.dataset.dpFokus || '';
    const zoom = punkt.dataset.dpZoom || '';
    const setzeLage = (ziel) => {
      setzeFokusUndZoom(ziel, fokus, zoom);
      // Mit dem Farbbild tritt der Hinweis ab - im selben Zug, in dem das Bild aufblendet.
      if (hinweis) hinweis.classList.add('dp-ohne-bild--weg');
    };

    /*
     * Das Kartenbild wechselt weich (dp-bildtausch.js): das neue Bild wird
     * vorgeladen, das alte blendet darueber aus. Stand die Kachel leer, weil
     * der Artikel nur Bilder mit Farb-Marke traegt, blendet das Bild der
     * Farbe aus der leeren Flaeche heraus auf.
     *
     * Liegt die Wischbahn darueber (dp-kachelbilder.js), ist das Kartenbild
     * gar nicht zu sehen - dann wird es nur still nachgefuehrt, die Blende
     * uebernimmt die Bahn.
     */
    const mitBahn = kachel.querySelector('.dp-medien--bahn');
    if (mitBahn) {
      // srcset muss weichen, sonst waehlt der Browser weiter aus der alten Liste.
      bild.removeAttribute('srcset');
      bild.src = adresse;
      bild.classList.remove('dp-bildtausch--leer');
      setzeLage(bild);
    } else {
      tauscheBildWeich(bild, adresse, setzeLage);
    }

    /*
     * Das zweite Bild (beim Ueberfahren) gehoert zur zuvor gezeigten Farbe
     * und tritt ab - mit seiner eigenen Blende, erst danach aus dem Aufbau.
     */
    const zweites = kachel.querySelector('.resource-card__image--secondary');
    if (zweites instanceof HTMLElement && zweites.style.display !== 'none') {
      zweites.classList.add('dp-bildtausch--ab');
      window.setTimeout(() => {
        zweites.style.display = 'none';
      }, 400);
    }

    /*
     * Zeigt die Kachel ihre Bilder ueber die Wischbahn (dp-kachelbilder.js),
     * ist das getauschte Kartenbild gar nicht zu sehen - die Bahn liegt
     * darueber. Sie bekommt den Wunsch gemeldet und gleitet zum Bild der
     * Farbe. Ohne Bahn aendert der Tausch oben das sichtbare Bild direkt.
     */
    kachel.dispatchEvent(
      new CustomEvent('dp:zeigeBild', { bubbles: true, detail: { quelle: adresse, fokus, zoom, farbe: punkt.dataset.dpFarbe || '' } })
    );
  },
  true
);

/**
 * Weniger Punkte, wenn sie an den Preis stossen.
 *
 * Die festen Schwellen im Stilblatt rechnen mit der Breite der Kachel und
 * greifen erst ab vier Farben. In besonders schmalen Kacheln - der Reihe
 * "Derzeit beliebt" im Handymenue etwa - stiessen aber schon drei Punkte an
 * einen laengeren Preis. Hier wird deshalb nachgemessen: Ragt der vorderste
 * Punkt an den Preistext heran, tritt der letzte Punkt zurueck, und das Plus
 * zeigt an, dass es mehr Farben gibt - so lange, bis wieder Luft ist. Ein
 * Punkt bleibt immer stehen.
 */
const LUFT_ZUM_PREIS = 6;

/** @param {Element} reihe */
function passePunkteAn(reihe) {
  if (!(reihe instanceof HTMLElement)) return;
  const preis = reihe.parentElement && reihe.parentElement.querySelector('.price');
  const mehr = reihe.querySelector('.dp-farben__mehr');
  const punkte = [...reihe.querySelectorAll('.dp-farbe')];

  for (const punkt of punkte) punkt.classList.remove('dp-farbe--zuviel');
  reihe.classList.remove('dp-farben--gekuerzt');
  if (!preis || !mehr || !reihe.getClientRects().length) return;

  const text = document.createRange();
  text.selectNodeContents(preis);
  const preisRand = text.getBoundingClientRect();
  if (!preisRand.width) return;

  /** Stoesst der vorderste sichtbare Punkt (oder das Plus) an den Preis? */
  const stoesst = () => {
    const sichtbar = [...reihe.children].filter((kind) => kind.getClientRects().length);
    if (!sichtbar.length) return false;
    const vorn = sichtbar.reduce((a, b) => (a.getBoundingClientRect().left < b.getBoundingClientRect().left ? a : b));
    const rand = vorn.getBoundingClientRect();
    const gleicheHoehe = rand.top < preisRand.bottom && rand.bottom > preisRand.top;
    return gleicheHoehe && rand.left < preisRand.right + LUFT_ZUM_PREIS;
  };

  let sichtbare = punkte.filter((punkt) => punkt.getClientRects().length);
  while (sichtbare.length > 1 && stoesst()) {
    reihe.classList.add('dp-farben--gekuerzt');
    sichtbare[sichtbare.length - 1].classList.add('dp-farbe--zuviel');
    sichtbare = punkte.filter((punkt) => punkt.getClientRects().length);
  }
}

/*
 * Gemessen wird, sobald eine Kachel sichtbar wird oder ihre Breite aendert -
 * auch im zunaechst verborgenen Handymenue, das erst beim Oeffnen eine Groesse
 * bekommt. Beobachtet wird die Kachel, gemeint ist ihre Punktreihe.
 */
const punkteBeobachter = new ResizeObserver((eintraege) => {
  for (const eintrag of eintraege) {
    const reihe = eintrag.target.querySelector(':scope > .dp-farben');
    if (reihe) passePunkteAn(reihe);
  }
});

function beobachtePunkte() {
  for (const reihe of document.querySelectorAll('.dp-farben')) {
    if (!(reihe instanceof HTMLElement) || reihe.dataset.dpPunkteBeobachtet !== undefined) continue;
    reihe.dataset.dpPunkteBeobachtet = '';
    if (reihe.parentElement) punkteBeobachter.observe(reihe.parentElement);
  }
}

beobachtePunkte();
document.addEventListener('dp:nachschub', beobachtePunkte);
